/**
 * The translation panel for server-rendered text (PM bubbles, Twig posts and
 * comments, mod support messages). toggleTranslation() inserts the panel
 * after an anchor element and removes it on the next call.
 *
 * The footer (target select and Hide) stays mounted while a new target
 * loads, so the select keeps focus. Only the body changes.
 */

import { focusFirstRendered } from './focus-fallback.js';
import { TOUCH_TARGET } from './touch-target.js';
import {
    getTargetLanguage,
    languageName,
    saveTargetLanguage,
    targetLanguageOptions,
    translateText,
} from './translator.js';

/* lucide-icons: languages, loader-2, x, rotate-ccw */

const PANEL_ATTR = 'data-translation-panel';

const LINK_BUTTON = `relative shrink-0 inline-flex items-center gap-1 rounded px-1 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 ${TOUCH_TARGET}`;

function label(key, fallback, params) {
    return window.tOr(`common.${key}`, fallback, params);
}

function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
}

function icon(name, className) {
    const node = el('i', className);
    node.setAttribute('data-lucide', name);
    node.setAttribute('aria-hidden', 'true');
    return node;
}

function renderBody(body, children) {
    body.replaceChildren(...children);
    window.LucideIcons?.initialize(body);
}

function loadingRow() {
    const row = el('div', 'flex items-center gap-2 text-gray-500 dark:text-gray-400');
    row.appendChild(icon('loader-2', 'w-4 h-4 motion-safe:animate-spin'));
    row.appendChild(el('span', '', label('translation_translating', 'Translating…')));
    return [row];
}

function errorRow(retry) {
    const row = el('div', 'flex flex-wrap items-center gap-2');
    row.appendChild(el('span', 'text-red-600 dark:text-red-400', label('translation_failed', "Translation isn't available right now.")));
    const again = el('button', LINK_BUTTON);
    again.type = 'button';
    again.appendChild(icon('rotate-ccw', 'w-3.5 h-3.5'));
    again.appendChild(el('span', '', label('try_again', 'Try again')));
    again.addEventListener('click', retry);
    row.appendChild(again);
    return [row];
}

function resultRows(result, target) {
    const text = el('p', 'm-0 whitespace-pre-wrap break-words text-gray-800 dark:text-gray-100', result.text);
    text.dir = 'auto';
    const nodes = [text];
    if (result.truncated) {
        nodes.push(el('p', 'm-0 mt-1 text-xs italic text-gray-500 dark:text-gray-400', label('translation_truncated', 'Only the first part of this text was translated.')));
    }
    if (result.detectedSource && result.detectedSource !== target) {
        const language = languageName(result.detectedSource);
        // tOr interpolates a catalog string but returns the fallback as it is.
        nodes.push(el('p', 'm-0 mt-1 text-xs text-gray-400 dark:text-gray-500',
            label('translation_from', 'Translated from {language}', { language }).replace('{language}', language)));
    }
    return nodes;
}

function footer(panel, target, onTarget) {
    const row = el('div', 'mt-1.5 flex items-center justify-between gap-2');
    const wrap = el('label', `relative inline-flex min-w-0 items-center gap-1 ${TOUCH_TARGET}`);
    wrap.appendChild(icon('languages', 'w-3.5 h-3.5 shrink-0 text-gray-400 dark:text-gray-500'));
    wrap.appendChild(el('span', 'sr-only', label('translation_target', 'Translate to')));
    const select = el('select', 'relative z-10 min-w-0 max-w-[9rem] cursor-pointer truncate rounded border-0 bg-transparent py-0 pl-0 pr-6 text-base text-gray-500 sm:text-xs focus:ring-2 focus:ring-brand dark:text-gray-400');
    for (const option of targetLanguageOptions()) {
        const node = el('option', '', option.name);
        node.value = option.code;
        node.selected = option.code === target;
        select.appendChild(node);
    }
    select.addEventListener('change', () => onTarget(select.value));
    wrap.appendChild(select);

    const hide = el('button', LINK_BUTTON);
    hide.type = 'button';
    hide.setAttribute('aria-label', label('translation_hide', 'Hide translation'));
    hide.title = label('translation_hide', 'Hide translation');
    hide.appendChild(icon('x', 'w-3.5 h-3.5'));
    hide.addEventListener('click', () => removePanel(panel));

    row.append(wrap, hide);
    return row;
}

function removePanel(panel) {
    const anchor = panel.previousElementSibling;
    const hadFocus = panel.contains(document.activeElement);
    panel.remove();
    anchor?.dispatchEvent(new CustomEvent('translation-closed', { bubbles: true, detail: { hadFocus } }));
}

/**
 * Show the translation of `text` in a panel after `anchor`, or remove the
 * panel when one is already open there.
 *
 * @param {Element} anchor
 * @param {string} text
 * @param {{className?: string, to?: string}} [options] - extra classes for the panel box, and a fixed target that is not saved as the viewer's default
 * @returns {boolean} whether a panel is open after the call
 */
export function toggleTranslation(anchor, text, options = {}) {
    const open = openPanel(anchor);
    if (open) {
        removePanel(open);
        return false;
    }
    if (!text || !text.trim()) {
        return false;
    }

    const panel = el('div', `mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-start dark:border-gray-700 dark:bg-gray-800/60 ${options.className || ''}`);
    panel.setAttribute(PANEL_ATTR, '');
    const body = el('div');
    body.setAttribute('aria-live', 'polite');

    let request = 0;
    const run = async (target) => {
        const id = ++request;
        renderBody(body, loadingRow());
        try {
            const result = await translateText(text, { to: target });
            if (id === request && panel.isConnected) {
                renderBody(body, resultRows(result, target));
            }
        } catch {
            if (id === request && panel.isConnected) {
                // The new body removes the Try again button, so the panel keeps focus.
                renderBody(body, errorRow(() => {
                    focusFirstRendered(panel);
                    run(target);
                }));
            }
        }
    };

    const first = options.to || getTargetLanguage();
    panel.append(body, footer(panel, first, (target) => {
        if (!options.to) {
            saveTargetLanguage(target);
        }
        run(target);
    }));
    anchor.insertAdjacentElement('afterend', panel);
    window.LucideIcons?.initialize(panel);
    run(first);

    return true;
}

/** The open panel after `anchor`, or null. */
export function openPanel(anchor) {
    const next = anchor?.nextElementSibling;
    return next && next.hasAttribute(PANEL_ATTR) ? next : null;
}

/** Remove the panel after `anchor`, if one is open. */
export function closeTranslation(anchor) {
    const panel = openPanel(anchor);
    if (panel) {
        removePanel(panel);
    }
}
